// @flow
/**
 * Utilities that deal with phone, leveraging client-side Griffin 🎉
 */

import griffinPhone from 'griffin-paypal/phone'
import getGriffinMetadata from './griffin-utils'
import { isEmpty } from 'lodash'

const NATIONAL_STYLE = 'NATIONAL'
const INTERNATIONAL_STYLE = 'INTERNATIONAL'

export function formatPhoneNumber({
  countryCode = '',
  nationalNumber = '',
  extension = '',
  style = NATIONAL_STYLE,
}: Object) {
  const griffinMetadata = getGriffinMetadata()
  // safeguard against undefined bc we grab griffin metadata from the store which
  // may not be available (especially in the case of portable p2p when the initial
  // store data is loaded asynchronously)
  if (!griffinMetadata) {
    return ''
  }
  const griffinPhoneHandler = griffinPhone.createHandler(griffinMetadata)
  return griffinPhoneHandler.formatPhoneNumber(
    {
      countryCode,
      nationalNumber,
      extension,
    },
    { style: griffinPhoneHandler[`PHONENUMBER_FORMAT_${style}`] }
  )
}

export function formatAsTyped(phoneNumber: string, countryCode: string) {
  const griffinMetadata = getGriffinMetadata()
  if (!griffinMetadata) {
    return ''
  }
  const griffinPhoneHandler = griffinPhone.createHandler(griffinMetadata)
  return griffinPhoneHandler.formatAsTyped(phoneNumber, { countryCode })
}

export function getExamplePhoneNumber(country: string) {
  if (!country) {
    const store = require('../stores/configureStore').default
    country = store.getState().server.country
  }

  const griffinMetadata = getGriffinMetadata()

  // safeguard against undefined bc we grab griffin metadata from the store which
  // may not be available (especially in the case of portable p2p when the initial
  // store data is loaded asynchronously)
  if (!griffinMetadata) {
    return ''
  }

  const griffinPhoneHandler = griffinPhone.createHandler(griffinMetadata)

  // FIXED_LINE_OR_MOBILE should return a valid phone number for all countries
  const examplePhone = griffinPhoneHandler.getExamplePhoneNumber(
    'FIXED_LINE_OR_MOBILE',
    { country }
  )
  return griffinPhoneHandler.formatPhoneNumber(examplePhone)
}

export function isPhoneNumberValid(phoneNumber: string, country: string) {
  if (!country) {
    const store = require('../stores/configureStore').default
    country = store.getState().server.country
  }

  const griffinMetadata = getGriffinMetadata()

  // griffin normalization throws an error when phoneNumber is '' (e.g. at app initialization)
  if (!griffinMetadata || !phoneNumber) {
    return false
  }

  const griffinPhoneHandler = griffinPhone.createHandler(griffinMetadata)
  const { errors = [] } = griffinPhoneHandler.normalizePhoneNumber(
    phoneNumber,
    { country }
  )

  // if it is a valid phone number, this errors array will be empty
  // TODO: handle the error codes that could be returned (https://github.paypal.com/Globalization-R/griffin-paypal/tree/develop/lib/client#validatephonenumber)
  return !errors.length
}

export function normalizePhoneNumber(phoneNumber: string, country?: string) {
  const griffinMetadata = getGriffinMetadata()

  if (!griffinMetadata) {
    return { errors: 'Missing griffinMetadata' }
  }

  const options = !isEmpty(country) ? { country } : undefined

  const griffinPhoneHandler = griffinPhone.createHandler(griffinMetadata)

  const normalizedPhone = griffinPhoneHandler.normalizePhoneNumber(
    phoneNumber,
    options
  )

  return normalizedPhone
}

export function getDisplayPhone(
  { phones = [] }: { phones: Array<ContactPhone> },
  { style }: Object = {}
) {
  const { phone_number: firstPhone } = phones[0] || {}
  let displayPhone
  if (typeof firstPhone === 'string') {
    displayPhone = firstPhone
  } else if (typeof firstPhone === 'object') {
    // try to pull out the properties
    const {
      country_code: countryCode = '',
      national_number: nationalNumber = '',
    } = firstPhone
    const partsExist = countryCode && nationalNumber
    displayPhone = partsExist
      ? formatPhoneNumber({ countryCode, nationalNumber, style })
      : ''
  } else {
    displayPhone = ''
  }
  return displayPhone
}

export function getGriffinPhoneHandler(griffinMetadata: GriffinMetadata = {}) {
  return griffinPhone.createHandler(griffinMetadata)
}

// Tries to parse it without the countryCode and formats phone number internationally,
// if that doesn't work then we try to parse the phone with the countryCode and
// format it nationally.
//
// Returns empty string in case of errors attempting to parse
export function parseAndFormatPhoneNumber(value: string, countryCode?: string) {
  let canonicalPhone

  // if Griffin can parse the number without explicit country string use it
  // and format it internationally
  try {
    canonicalPhone = normalizePhoneNumber(value)
  } catch (err) {
    canonicalPhone = { errors: err }
  }

  if (isEmpty(canonicalPhone.errors)) {
    const internationalCanonicalPhone = {
      ...canonicalPhone,
      ...{ style: INTERNATIONAL_STYLE },
    }
    return formatPhoneNumber(internationalCanonicalPhone)
  }

  // if not, default to the country code of the user typing the information
  // and format it nationally
  try {
    canonicalPhone = normalizePhoneNumber(value, countryCode)
  } catch (err) {
    canonicalPhone = { errors: err }
  }

  if (isEmpty(canonicalPhone.errors)) {
    const nationalCanonicalPhone = {
      ...canonicalPhone,
      ...{ style: NATIONAL_STYLE },
    }
    return formatPhoneNumber(nationalCanonicalPhone)
  }

  // if it's not parsable in any case, be sure to return a falsey value
  return ''
}

export function isPhoneNumberMaybe(value: string) {
  return typeof value !== 'string' || !value.includes('@')
}

export function getPhoneNumberFormatter(countryCode: string) {
  return function format(value: string) {
    if (isPhoneNumberMaybe(value)) {
      return parseAndFormatPhoneNumber(value, countryCode)
    }
    return value
  }
}
